import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { cookieManager } from '../../Constant/Utils';

const initialState = {
  token: '',
  track: null,
  artist: null,
  artist_album: null,
  loading: false,
  loadingAlbums: false,
};

export const getToken = createAsyncThunk('spotifySlice/getToken', async (_, { rejectWithValue }) => {
  try {
    let token = cookieManager.getCookie('token');
    if (token) {
      return token;
    }

    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/spotify/token`);

    if (response.data.success) {
      const accessToken = response.data.access_token;
      const expires_in = response.data.expires_in;
      const expirationMinutes = expires_in ? Math.floor(expires_in / 60) : 60;
      cookieManager.setCookie('token', accessToken, expirationMinutes);
      return accessToken;
    }

    return rejectWithValue(response.data.message || 'Failed to get Spotify token');
  } catch (error) {
    return rejectWithValue(
      error?.response?.data?.message || error?.message || 'Failed to get Spotify token'
    );
  }
});

export const getSongDetail = createAsyncThunk(
  'spotifySlice/getSongDetail',
  async (data, { rejectWithValue }) => {
    try {
      const token = cookieManager.getCookie('token');

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `https://api.spotify.com/v1/tracks/${data?.q}?type=track%2Cartist`,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      };

      const response = await axios.request(config);
      return {
        data: response.data,
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch track details');
    }
  }
);

export const getAlbumTracks = createAsyncThunk(
  'spotifySlice/getAlbumTracks',
  async (data, { rejectWithValue }) => {
    const headers = { Authorization: 'Bearer ' + cookieManager.getCookie('token') };
    let album;
    try {
      const response = await axios.get(`https://api.spotify.com/v1/albums/${data?.id}`, { headers });
      album = response.data;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch album');
    }

    // The album response embeds the first page of tracks; follow `next` for longer albums.
    const tracks = [...(album?.tracks?.items || [])];
    let next = album?.tracks?.next;
    try {
      while (next) {
        const response = await axios.get(next, { headers });
        tracks.push(...(response.data?.items || []));
        next = response.data?.next;
      }
    } catch {
      // keep the tracks we already have
    }

    return {
      album: { name: album?.name, images: album?.images || [] },
      tracks: tracks.filter(Boolean).map(track => ({ ...track, album })),
    };
  }
);

export const validateArtist = createAsyncThunk(
  'spotifySlice/validateArtist',
  async (data, { rejectWithValue }) => {
    try {
      let token = cookieManager.getCookie('token');

      let config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `https://api.spotify.com/v1/artists/${data?.id}`,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      };

      const response = await axios.request(config);
      return {
        isValid: true,
        data: response.data,
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue('Artist not found. Please check the artist URL and try again.');
      } else if (error.response?.status === 400) {
        return rejectWithValue('Invalid artist ID format.');
      } else {
        return rejectWithValue('Unable to verify artist. Please try again.');
      }
    }
  }
);

export const getArtist = createAsyncThunk(
  'spotifySlice/getArtist',
  async (data, { rejectWithValue }) => {
    try {
      let token = cookieManager.getCookie('token');
      let config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `https://api.spotify.com/v1/artists/${data?.id}`,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      };

      const response = await axios.request(config);
      return {
        data: response.data,
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue('Artist not found');
      } else if (error.response?.status === 400) {
        return rejectWithValue('Invalid artist ID');
      } else {
        return rejectWithValue('Failed to fetch artist data');
      }
    }
  }
);

export const getArtistAlbum = createAsyncThunk(
  'spotifySlice/getArtistAlbum',
  async (data, { rejectWithValue }) => {
    try {
      const token = cookieManager.getCookie('token');
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `https://api.spotify.com/v1/artists/${data.id}/albums`,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      };

      const response = await axios.request(config);
      return {
        data: response.data,
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch artist albums');
    }
  }
);

export const spotifySlice = createSlice({
  name: 'spotifySlice',
  initialState,
  reducers: {
    addTodo: (state, action) => {
      // request
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getToken.pending, state => {})
      .addCase(getToken.fulfilled, (state, action) => {
        state.token = action.payload;
      })
      .addCase(getToken.rejected, state => {})
      .addCase(getSongDetail.pending, state => {
        state.track = null;
        state.loading = true;
      })
      .addCase(getSongDetail.fulfilled, (state, action) => {
        state.track = action?.payload?.data;
        state.loading = false;
      })
      .addCase(getSongDetail.rejected, state => {
        state.track = null;
        state.loading = false;
      })
      .addCase(getArtist.pending, state => {})
      .addCase(getArtist.fulfilled, (state, action) => {
        state.artist = action?.payload?.data;
      })
      .addCase(getArtist.rejected, state => {})
      .addCase(getArtistAlbum.pending, state => {
        state.loadingAlbums = true;
      })
      .addCase(getArtistAlbum.fulfilled, (state, action) => {
        state.artist_album = action?.payload?.data?.items;
        state.loadingAlbums = false;
      })
      .addCase(getArtistAlbum.rejected, state => {
        state.loadingAlbums = false;
      })
      .addCase(validateArtist.pending, state => {})
      .addCase(validateArtist.fulfilled, state => {
        // Don't update state for validation, just used for checking
      })
      .addCase(validateArtist.rejected, state => {
        // Don't update state for validation errors
      });
  },
});

export const { addTodo } = spotifySlice.actions;

export default spotifySlice.reducer;
